import { Button, Table } from 'antd';
import { useContext } from 'react';

import {
  ConfirmDeletingElement,
  EmptyComponentWithDataTestId,
} from '@app/components';
import { AddUserIntegrationsModal } from '@app/components/UserIntegrationsTab/components/dialog/AddUserIntegrationsModal';
import { useUserIntegrationEffects } from '@app/components/UserIntegrationsTab/hooks/useUserIntegrationEffects';
import { useUserIntegrationsTab } from '@app/components/UserIntegrationsTab/hooks/useUserIntegrationsTab';
import { adaptDataToTable } from '@app/components/UserIntegrationsTab/utils/adaptDataToTable';
import { FRONT_WARNING, INTEGRATIONS_HEADER, PREFIX } from '@app/constants';
import { NotificationContext } from '@app/providers';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { getPaginationItemRenderWithDataTestIds } from '@app/utils';

import type { RowSelectionType } from 'antd/es/table/interface';
import type { FC, Key } from 'react';

import '@app/components/UserIntegrationsTab/styles/UserIntegrationsTab.desktop.scss';

export const UserIntegrationsTab: FC = () => {
  const { isMobile } = useUserIntegrationsTab();

  const { showNotification } = useContext(NotificationContext);

  const {
    integrations,
    isGetUserSocialIntegrationsLoading,
    availableIntegrationsForAddition,
    isAvailableIntegrationsForAdditionLoading,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDeleteIntegration,
    handleAddIntegrations,
    isAddUserIntegrationsModalOpen,
    setIsAddUserIntegrationsModalOpen,
    selectedRowKeys,
    setSelectedRowKeys,
    selectedAddedIntegrations,
    setSelectedAddedIntegrations,
    handleCloseModal,
  } = useUserIntegrationEffects();

  return (
    <div className={`${PREFIX}user-integrations-tab`}>
      <div className="user-integrations-tab-container">
        <Button
          type="primary"
          onClick={() => {
            setIsAddUserIntegrationsModalOpen(true);
          }}
          className="add-integration-button"
          data-testid="add-integration"
        >
          Добавить
        </Button>
        <Button
          type="primary"
          onClick={() => {
            if (selectedRowKeys.length === 0) {
              showNotification(
                'Не выбраны интеграции для удаления',
                FRONT_WARNING
              );
              return;
            }

            setIsDeleteModalOpen(true);
          }}
          data-testid="delete-integration-button"
        >
          Удалить
        </Button>
      </div>

      <Table
        loading={isGetUserSocialIntegrationsLoading}
        columns={INTEGRATIONS_HEADER}
        className="integrations-table"
        rowClassName="integration-item"
        rowKey="Key"
        dataSource={adaptDataToTable(
          integrations,
          setIsDeleteModalOpen,
          setSelectedRowKeys
        )}
        locale={{
          emptyText: <EmptyComponentWithDataTestId />,
        }}
        pagination={{
          itemRender: getPaginationItemRenderWithDataTestIds,
          size: isMobile ? 'small' : 'default',
          showSizeChanger: true,
          position: isMobile ? ['bottomCenter'] : ['bottomLeft'],
          showTotal: (total, range) => (
            <span data-testid="pagination-info">{`Показаны ${range[0]}-${range[1]} из ${total}`}</span>
          ),
        }}
        rowSelection={{
          type: 'checkbox' as RowSelectionType,
          onChange: (selectedRowKeys: Key[]) => {
            setSelectedRowKeys(selectedRowKeys.map(String));
          },
          selectedRowKeys,
          columnTitle: (checkboxNode: React.ReactNode) =>
            generateSpanWithDataTestId(
              checkboxNode,
              'all-integrations-checkbox'
            ),
          renderCell: (checked, record, index, originNode) =>
            generateSpanWithDataTestId(originNode, 'integration-checkbox'),
        }}
      />

      <AddUserIntegrationsModal
        options={availableIntegrationsForAddition || []}
        isLoading={isAvailableIntegrationsForAdditionLoading}
        selectedItems={selectedAddedIntegrations}
        setSelectedItems={setSelectedAddedIntegrations}
        isOpen={isAddUserIntegrationsModalOpen}
        onSave={handleAddIntegrations}
        onCancel={handleCloseModal}
      />

      <ConfirmDeletingElement
        modalOpen={isDeleteModalOpen}
        setModalOpen={setIsDeleteModalOpen}
        handleDelete={handleDeleteIntegration}
        content="Вы уверены, что хотите удалить выбранные интеграции?"
        title="Удаление интеграции"
        modalDataTestId="delete-integration-popup"
        titleDataTestId="delete-integration-popup-title"
        descriptionDataTestId="delete-integration-popup-description"
      />
    </div>
  );
};
